import {
  BARCODE_SAMPLE_CONTENT,
  ICON_LIBRARY,
  LABEL_HEIGHT_MM,
  LABEL_WIDTH_MM,
  LabelElement,
  LabelTemplate,
  BarcodeType,
  IconName,
} from '@/types/label';

type UnknownRecord = Record<string, unknown>;

const VALID_BARCODE_TYPES = new Set<BarcodeType>([
  'code128',
  'code39',
  'ean13',
  'ean8',
  'upca',
  'code93',
  'itf14',
  'datamatrix',
  'pdf417',
  'gs1-128',
  'interleaved2of5',
  'codabar',
  'msi',
  'pharmacode',
  'telepen',
]);

const VALID_ROTATIONS = new Set([0, 90, 180, 270]);
const VALID_SHAPE_TYPES = new Set(['rectangle', 'circle', 'line', 'triangle']);
const VALID_ICON_NAMES = new Set<string>(ICON_LIBRARY);

interface SanitizeTemplateOptions {
  fallbackId?: string;
  fallbackName?: string;
  fallbackWidth?: number;
  fallbackHeight?: number;
  fallbackCreatedAt?: number;
  fallbackUpdatedAt?: number;
}

const isRecord = (value: unknown): value is UnknownRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const toFiniteNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const getNestedValue = (parent: UnknownRecord, key: string): unknown => {
  const nested = parent[key];
  return nested;
};

const getRawPositionValue = (rawElement: UnknownRecord, axis: 'x' | 'y'): unknown => {
  if (isRecord(rawElement.position)) {
    const nestedValue = getNestedValue(rawElement.position, axis);
    if (nestedValue !== undefined) return nestedValue;
  }
  return getNestedValue(rawElement, axis);
};

const getRawSizeValue = (rawElement: UnknownRecord, dimension: 'width' | 'height'): unknown => {
  if (isRecord(rawElement.size)) {
    const nestedValue = getNestedValue(rawElement.size, dimension);
    if (nestedValue !== undefined) return nestedValue;
  }
  return getNestedValue(rawElement, dimension);
};

const getDefaultSize = (type: LabelElement['type']) => {
  switch (type) {
    case 'barcode':
      return { width: 25, height: 8 };
    case 'qrcode':
      return { width: 12, height: 12 };
    case 'image':
      return { width: 10, height: 10 };
    case 'shape':
      return { width: 10, height: 10 };
    case 'icon':
      return { width: 5, height: 5 };
    case 'text':
    default:
      return { width: 20, height: 5 };
  }
};

const normalizeElementType = (rawElement: UnknownRecord): LabelElement['type'] => {
  const rawType = toOptionalString(rawElement.type)?.toLowerCase();
  const rawBarcodeFormat = toOptionalString(rawElement.barcodeFormat)?.toLowerCase();

  if (rawType === 'qr' || rawType === 'qrcode' || rawType === 'qr-code') {
    return 'qrcode';
  }

  if (rawType === 'barcode' && (rawBarcodeFormat === 'qr' || rawBarcodeFormat === 'qrcode' || rawBarcodeFormat === 'qr-code')) {
    return 'qrcode';
  }

  if (rawType === 'rectangle' || rawType === 'circle' || rawType === 'line' || rawType === 'triangle') {
    return 'shape';
  }

  if (rawType === 'barcode' || rawType === 'image' || rawType === 'shape' || rawType === 'icon') {
    return rawType;
  }

  return 'text';
};

const normalizeBarcodeType = (value: unknown): BarcodeType => {
  const normalized = toOptionalString(value)?.toLowerCase();
  if (!normalized) return 'code128';

  switch (normalized) {
    case 'ean-13':
      return 'ean13';
    case 'ean-8':
      return 'ean8';
    case 'upc-a':
      return 'upca';
    case 'code-39':
      return 'code39';
    case 'code-93':
      return 'code93';
    case 'code-128':
      return 'code128';
    case 'itf-14':
      return 'itf14';
    case 'interleaved-2-of-5':
    case 'interleaved 2 of 5':
      return 'interleaved2of5';
    default:
      return VALID_BARCODE_TYPES.has(normalized as BarcodeType) ? normalized as BarcodeType : 'code128';
  }
};

const normalizeIconName = (value: unknown): IconName => {
  const normalized = toOptionalString(value)?.toLowerCase().replace(/\s+/g, '-') ?? 'package';
  return (VALID_ICON_NAMES.has(normalized) ? normalized : 'package') as IconName;
};

export const sanitizeLabelElement = (
  rawElement: unknown,
  index: number,
  labelWidth: number,
  labelHeight: number,
): LabelElement | null => {
  if (!isRecord(rawElement)) return null;

  const type = normalizeElementType(rawElement);
  const defaultSize = getDefaultSize(type);
  const width = clamp(
    toFiniteNumber(getRawSizeValue(rawElement, 'width'), defaultSize.width),
    2,
    Math.max(2, labelWidth),
  );
  const height = clamp(
    toFiniteNumber(getRawSizeValue(rawElement, 'height'), defaultSize.height),
    2,
    Math.max(2, labelHeight),
  );
  const x = clamp(
    toFiniteNumber(getRawPositionValue(rawElement, 'x'), 0),
    0,
    Math.max(0, labelWidth - width),
  );
  const y = clamp(
    toFiniteNumber(getRawPositionValue(rawElement, 'y'), 0),
    0,
    Math.max(0, labelHeight - height),
  );
  const base = {
    id: toOptionalString(rawElement.id) ?? `element-${Date.now()}-${index}`,
    position: { x, y },
    size: { width, height },
    locked: rawElement.locked === true,
    visible: rawElement.visible !== false,
    rotation: clamp(toFiniteNumber(rawElement.rotation, 0), -360, 360),
    zIndex: toFiniteNumber(rawElement.zIndex, index),
    field: toOptionalString(rawElement.field),
  };

  if (type === 'barcode') {
    const barcodeType = normalizeBarcodeType(rawElement.barcodeType ?? rawElement.barcodeFormat ?? rawElement.format);
    return {
      ...base,
      type: 'barcode',
      content: toOptionalString(rawElement.content) ?? BARCODE_SAMPLE_CONTENT[barcodeType] ?? '{{Barcode}}',
      includeText: rawElement.includeText !== false,
      barcodeType,
      barScale: clamp(toFiniteNumber(rawElement.barScale, 2), 0.5, 8),
      barHeightRatio: clamp(toFiniteNumber(rawElement.barHeightRatio, 0.8), 0.1, 1),
      quietZone: clamp(toFiniteNumber(rawElement.quietZone, 2), 0, 20),
      foregroundColor: toOptionalString(rawElement.foregroundColor),
      backgroundColor: toOptionalString(rawElement.backgroundColor),
      textSize: clamp(toFiniteNumber(rawElement.textSize, 8), 6, 16),
    };
  }

  if (type === 'qrcode') {
    return {
      ...base,
      type: 'qrcode',
      content: toOptionalString(rawElement.content) ?? '{{QRData}}',
      foregroundColor: toOptionalString(rawElement.foregroundColor),
      backgroundColor: toOptionalString(rawElement.backgroundColor),
    };
  }

  if (type === 'shape') {
    const shapeType = toOptionalString(rawElement.shapeType ?? rawElement.type)?.toLowerCase() ?? 'rectangle';
    return {
      ...base,
      type: 'shape',
      shapeType: (VALID_SHAPE_TYPES.has(shapeType) ? shapeType : 'rectangle') as 'rectangle' | 'circle' | 'line' | 'triangle',
      fillColor: toOptionalString(rawElement.fillColor ?? rawElement.backgroundColor) ?? 'transparent',
      strokeColor: toOptionalString(rawElement.strokeColor ?? rawElement.borderColor ?? rawElement.color) ?? '#000000',
      strokeWidth: clamp(toFiniteNumber(rawElement.strokeWidth ?? rawElement.borderWidth, 1), 1, 20),
      borderRadius: clamp(toFiniteNumber(rawElement.borderRadius, 0), 0, 999),
    };
  }

  if (type === 'image') {
    const objectFit = toOptionalString(rawElement.objectFit)?.toLowerCase();
    return {
      ...base,
      type: 'image',
      src: toOptionalString(rawElement.src) ?? '',
      objectFit: objectFit === 'cover' || objectFit === 'fill' ? objectFit : 'contain',
      borderRadius: clamp(toFiniteNumber(rawElement.borderRadius, 0), 0, 999),
    };
  }

  if (type === 'icon') {
    return {
      ...base,
      type: 'icon',
      iconName: normalizeIconName(rawElement.iconName ?? rawElement.content ?? rawElement.name),
      color: toOptionalString(rawElement.color) ?? '#000000',
    };
  }

  return {
    ...base,
    type: 'text',
    content: toOptionalString(rawElement.content) ?? 'Text',
    fontSize: clamp(toFiniteNumber(rawElement.fontSize, 12), 6, 72),
    fontFamily: toOptionalString(rawElement.fontFamily) ?? 'Arial',
    fontWeight: rawElement.fontWeight === 'bold' ? 'bold' : 'normal',
    textAlign: rawElement.textAlign === 'center' || rawElement.textAlign === 'right' ? rawElement.textAlign : 'left',
    color: toOptionalString(rawElement.color) ?? '#000000',
  };
};

export const sanitizeLabelElements = (
  rawElements: unknown[],
  labelWidth: number,
  labelHeight: number,
): LabelElement[] => rawElements
  .map((element, index) => sanitizeLabelElement(element, index, labelWidth, labelHeight))
  .filter(Boolean) as LabelElement[];

export const sanitizeLabelTemplate = (
  rawTemplate: unknown,
  options: SanitizeTemplateOptions = {},
): LabelTemplate | null => {
  if (!isRecord(rawTemplate)) return null;

  const width = clamp(
    toFiniteNumber(rawTemplate.width, options.fallbackWidth ?? LABEL_WIDTH_MM),
    1,
    500,
  );
  const height = clamp(
    toFiniteNumber(rawTemplate.height, options.fallbackHeight ?? LABEL_HEIGHT_MM),
    1,
    500,
  );
  const elements = sanitizeLabelElements(
    Array.isArray(rawTemplate.elements) ? rawTemplate.elements : [],
    width,
    height,
  );
  const rotation = toFiniteNumber(rawTemplate.labelRotation, 0);

  return {
    id: toOptionalString(rawTemplate.id) ?? options.fallbackId ?? `template-${Date.now()}`,
    name: toOptionalString(rawTemplate.name) ?? options.fallbackName ?? 'Untitled Label',
    width,
    height,
    elements,
    createdAt: toFiniteNumber(rawTemplate.createdAt, options.fallbackCreatedAt ?? Date.now()),
    updatedAt: toFiniteNumber(rawTemplate.updatedAt, options.fallbackUpdatedAt ?? Date.now()),
    isGlobal: rawTemplate.isGlobal === true,
    category: toOptionalString(rawTemplate.category),
    labelRotation: (VALID_ROTATIONS.has(rotation) ? rotation : undefined) as 0 | 90 | 180 | 270 | undefined,
    fieldConfig: isRecord(rawTemplate.fieldConfig) ? rawTemplate.fieldConfig as LabelTemplate['fieldConfig'] : undefined,
  };
};